import {MouseEvent, useContext, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import Popover from 'patient-ping-remedy/packages/popover';
import {Menu, MenuItem, MenuItemIcon, MenuItemSpacer, MenuItemTypography} from 'patient-ping-remedy/packages/menu';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAlertStore} from '@/store/alert_store';
import {FilterResponse} from '@/api/dto/filters';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import DeleteModal from '@common/DeleteModal';
import {Action, SavedLabel, PlaceholderLabel, Button, Wrapper, HoverMenuItem, PlaceholderIcon} from '@common/FilterSelect.styles';
import Helpers from '@/helpers/helpers';

type Props = {
  id: string;
  filters: FilterResponse[];
  savedFilter?: FilterResponse;
  onChange(isOpen: boolean, filter?: FilterResponse): void;
}

export default function FilterSelect({ id, filters, savedFilter, onChange }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const [showPopover, setShowPopover] = useState(false);
  const [deletingFilter, setDeletingFilter] = useState<FilterResponse | null>(null);

  const { mutateAsync: deleteFilter } = useMutation({
    mutationFn: async (filter: FilterResponse) => carecoApi?.deleteFilter(filter.id),
    onSuccess(_, filter) {
      addAlert({
        type: ToastType.SUCCESS,
        content: `${filter.name} deleted successfully.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['filters', id] });
      onChange(false, filter.id === savedFilter?.id ? undefined : savedFilter);
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to delete view. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const handleChange = (e: MouseEvent<HTMLElement>, isOpen: boolean, filter?: FilterResponse) => {
    e.stopPropagation();
    setShowPopover(false);
    onChange(isOpen, filter);
  };

  const handleDelete = (e: MouseEvent<HTMLElement>, filter: FilterResponse) => {
    e.stopPropagation();
    setShowPopover(false);
    setDeletingFilter(filter);
  };

  return (
    <Wrapper>
      <Popover
        toggle={() => setShowPopover(!showPopover)}
        isOpen={showPopover}
        position="bottom-start"
        content={(
          <Menu onHide={() => setShowPopover(false)} width="30rem" marginTop marginBottom>
            {filters.sort((a, b) => a.id - b.id).map((filter) => (
              <HoverMenuItem key={filter.id} onClick={(e) => handleChange(e, false, filter)}>
                <MenuItemTypography active={filter.id === savedFilter?.id}>{filter.name}</MenuItemTypography>
                <MenuItemSpacer />
                <Action onClick={(e) => handleChange(e, true, filter)}>
                  <Icon iconClass={ICONS.pencil} iconSize="lg" weight="far" />
                </Action>
                <Action onClick={(e) => handleDelete(e, filter)}>
                  <Icon iconClass={ICONS['trash-alt']} iconSize="lg" weight="far" />
                </Action>
              </HoverMenuItem>
            ))}
            <MenuItem onClick={(e) => handleChange(e, true)}>
              <MenuItemIcon iconClass={ICONS.plus} />
              <MenuItemTypography>New view</MenuItemTypography>
            </MenuItem>
          </Menu>
        )}
      >
        <Button>
          {savedFilter ? (
            <>
              <SavedLabel>{savedFilter.name}</SavedLabel>
              <Action onClick={(e) => handleChange(e, false)}>
                <Icon iconClass={ICONS.times} iconSize="lg" weight="far" />
              </Action>
            </>
          ) : (
            <>
              <PlaceholderLabel>Select saved view</PlaceholderLabel>
              <PlaceholderIcon>
                <Icon iconClass={ICONS['angle-down']} iconSize="lg" weight="far" />
              </PlaceholderIcon>
            </>
          )}
        </Button>
      </Popover>
      {deletingFilter && (
        <DeleteModal
          title="View"
          isOpen
          close={() => setDeletingFilter(null)}
          confirm={() => deleteFilter(deletingFilter)}
        />
      )}
    </Wrapper>
  );
}
